import React, { useEffect, useState } from "react";
import {
  Button,
  Grid,
  InputAdornment,
  TextField,
  Typography,
  MenuItem,
  Divider,
} from "@mui/material";

import * as Yup from "yup";
import { Formik } from "formik";
import moment from "moment";
import "moment-timezone";
import { Box } from "@mui/system";
import { handlePaging } from "../../../../components/api/APIUtilities";
import { postNotification } from "../../../../components/utilities/Notifications";
import { postData } from "../../../../components/utilities/UtilityFunctions";
import GenericPicker from "../../../../components/utilities/GenericPicker";
import { CardImagePreview } from "../../../cards/components/CardCard";
import {
  BCPEye,
  BCPRotateRight,
} from "../../../../components/icons/bcp/BCPIcons";

export default function DeckSelection(props) {
  const {
    authContext,
    event,
    player,
    setProcessing,
    handleSetPlayer,
    label,
    labelVariant,
  } = props;
  const listLimit = 10;

  const initialValues = {
    listId: player?.listId || player?.armyListObjectId || "",
  };

  const [lists, setLists] = useState(null);
  const [list, setList] = useState(player?.armyList);
  const [nextKey, setNextKey] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  const listQueryParams = () => {
    return {
      limit: listLimit,
      gameSystemId: event?.gameSystem?.id,
      userId: player?.userId || authContext?.authState?.identity?.userId,
      includeNonPublic:
        !player?.id ||
        player?.userId === authContext?.authState?.identity?.userId,
      sortKey: "_created_at",
      sortAscending: false,
      "expand[]": [
        "gameSystem",
        "user",
        "event",
        "player",
        "army",
        "subFaction",
        "character",
      ],
    };
  };

  const fetchMore = async () => {
    if (!hasMore) return;
    setProcessing(true);
    try {
      var apiLists = await handlePaging(
        `/armylists`,
        listQueryParams(),
        nextKey,
        setNextKey
      );

      if (apiLists.length !== listLimit) {
        setHasMore(false);
      }
      if (apiLists.length) {
        setLists([...lists, ...apiLists]);
      }
    } catch (err) {
      postNotification("error", err);
    }
    setProcessing(false);
  };
  const fetchInitial = async () => {
    setHasMore(true);
    if (lists) {
      setProcessing(true);
    }
    var apiLists = await handlePaging(
      `/armylists`,
      listQueryParams(),
      null,
      setNextKey
    );

    if (!apiLists.length && !player?.armyList) {
      postNotification(
        "warning",
        `${player?.id ? "Player has" : "You have"} no ${
          event?.gameSystem?.listLabel
            ? `${event?.gameSystem?.listLabel}s`
            : "lists"
        } assigned to account.`
      );
    }
    if (apiLists.length !== listLimit) {
      setHasMore(false);
    }

    setLists(apiLists);
    setProcessing(false);
  };

  const cleanList = (_list, _player) => {
    let newList = _list || {};

    newList.faction =
      _list?.faction || _list?.army || _player?.faction || _player?.army;
    newList.factionId =
      _list?.factionId ||
      _list?.armyId ||
      _player?.factionId ||
      _player?.armyId;

    newList.character = _list?.character || _list?.character;
    newList.characterId = _list?.characterId || _list?.characterId;
    return newList;
  };

  useEffect(() => {
    fetchInitial();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    let newList = cleanList(player?.armyList, player);

    setList(newList);
  }, [player]);

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={Yup.object().shape({
          listId: Yup.string().required("Required"),
        })}
        onSubmit={async (values, { setFieldValue }) => {
          try {
            setProcessing(true);
            var apiData = await postData(`/armylists/${values.listId}/submit`, {
              body: {
                playerId: player?.id,
                eventId: event?.id,
              },
            });

            if (apiData?.id && handleSetPlayer) {
              handleSetPlayer({
                ...player,
                listId: apiData.id,

                // legacy
                armyId: apiData.factionId || apiData.armyId,
                army: apiData.faction || apiData.army,

                factionId: apiData.factionId || apiData.armyId,
                faction: apiData.faction || apiData.army,

                subFactionId: apiData.subFactionId,
                subFaction: apiData.subFaction,

                characterId: apiData.characterId,
                character: apiData.character,

                listStatus: apiData.listStatus || player.listStatus,
              });
              setFieldValue("listId", apiData.id);
            }
          } catch (err) {
            postNotification("error", err);
          }
          setProcessing(false);
        }}
      >
        {({ errors, handleChange, handleSubmit, touched, values }) => (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant={labelVariant || "h4"}>
                  {label || "Deck Registration"}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <GenericPicker
                  list={(lists
                    ? [
                        list,
                        <Divider />,
                        ...lists,
                        hasMore
                          ? {
                              hasMore: (
                                <MenuItem>
                                  <Button
                                    fullWidth
                                    onClick={fetchMore}
                                    variant="outlined"
                                  >
                                    Load More
                                  </Button>
                                </MenuItem>
                              ),
                            }
                          : undefined,
                      ]
                    : [list]
                  ).map((_list, index) => {
                    if (!_list) return undefined;
                    if (_list.hasMore) return _list.hasMore;
                    return {
                      value: _list.id,
                      label: (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                          }}
                        >
                          <CardImagePreview
                            card={_list?.character}
                            superDense
                          />
                          <Typography sx={{ ml: 2 }}>
                            {_list.name || "Legacy Deck"}
                          </Typography>
                        </Box>
                      ),
                    };
                  })}
                  name="listId"
                  label="Deck List"
                  value={values.listId}
                  fullWidth
                  variant="outlined"
                  margin="none"
                  handleChange={(e) => {
                    handleChange(e);
                    let a = lists?.filter(
                      (el) => el.id === e.target.value
                    )?.[0];
                    setList(a);
                  }}
                  inclAny={false}
                  error={Boolean(touched.listId && errors.listId)}
                  helperText={touched.listId && errors.listId}
                  endAdornment={
                    <InputAdornment position="end">
                      <Button
                        sx={{ minWidth: "40px" }}
                        href={`/deck/${values.listId}`}
                        target="_blank"
                        disabled={!values.listId}
                      >
                        <BCPEye />
                      </Button>
                      <Button sx={{ minWidth: "40px" }} onClick={fetchInitial}>
                        <BCPRotateRight />
                      </Button>
                    </InputAdornment>
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  disabled
                  value={list?.faction?.name || list?.army?.name || ""}
                  label={event?.gameSystem?.factionLabel || "Faction"}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  disabled
                  value={list?.character?.name || ""}
                  label={event?.gameSystem?.subFactionLabel || "Character"}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  disabled
                  value={
                    list?.updated_at &&
                    (player?.listId || player?.armyListObjectId) === list.id
                      ? moment(list?.updated_at).format("MMMM Do YYYY, LT ZZ")
                      : ""
                  }
                  label={
                    list?.updated_at &&
                    (player?.listId || player?.armyListObjectId) === list.id
                      ? "Submitted"
                      : "Unsubmitted"
                  }
                  fullWidth
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  color="success"
                  fullWidth
                  variant="contained"
                  type="submit"
                  disabled={
                    values.listId ===
                    (player?.listId || player?.armyListObjectId)
                  }
                >
                  Update
                </Button>
              </Grid>
            </Grid>
          </form>
        )}
      </Formik>
    </>
  );
}
